import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, isAuthed, getUser } from "@/lib/api";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

/**
 * Heart toggle that lazily fetches the wishlist status for the given property.
 * For anonymous or non-guest users, clicking the heart routes them to the
 * guest auth page — the wishlist is a logged-in feature. `stopPropagation` is
 * on so the heart can sit inside a Link without triggering navigation.
 */
export default function WishlistHeart({ propertyId, className = "", size = "sm" }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const nav = useNavigate();
  const [inWishlist, setInWishlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const authed = isAuthed();
  const user = authed ? getUser() : null;
  const isGuest = user?.role === "guest";

  useEffect(() => {
    if (!isGuest || !propertyId) return;
    api.get("/wishlist/status", { params: { property_id: propertyId } })
      .then((r) => setInWishlist(!!r.data?.in_wishlist))
      .catch(() => {});
  }, [isGuest, propertyId]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isGuest) {
      toast.info(IT ? "Accedi per salvare le tue strutture preferite." : "Log in to save your favorites.");
      nav("/guest/auth?mode=login");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/wishlist/toggle", { property_id: propertyId });
      setInWishlist(!!r.data.added);
      toast.success(r.data.added
        ? (IT ? "Aggiunta ai preferiti ♥" : "Added to favorites ♥")
        : (IT ? "Rimossa dai preferiti" : "Removed from favorites"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setLoading(false); }
  };

  const dim = size === "lg" ? "w-9 h-9" : "w-8 h-8";
  const iconDim = size === "lg" ? "w-5 h-5" : "w-4 h-4";
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={loading}
      className={`${dim} inline-flex items-center justify-center rounded-full bg-background/90 backdrop-blur-sm border border-border shadow-sm hover:scale-110 transition-transform ${className}`}
      title={inWishlist ? (IT ? "Rimuovi dai preferiti" : "Remove from favorites") : (IT ? "Salva nei preferiti" : "Save to favorites")}
      data-testid={`wishlist-heart-${propertyId}`}
      aria-pressed={inWishlist}
    >
      <Heart className={`${iconDim} ${inWishlist ? "fill-primary text-primary" : "text-foreground/60"}`}/>
    </button>
  );
}
